import Vue from "vue";
import VueRouter from "vue-router";
import Home from "@/views/Home.vue";
import NotFound from "@/views/NotFound.vue";
import BookInfo from "@/views/BookInfo.vue";
import BookReader from "@/views/BookReader.vue";
import Contact from "@/views/Contact.vue";
import Bookshelf from "@/views/Bookshelf";
import UserProfile from "@/views/user/Profile";
import EditUserProfile from "@/views/user/EditProfile";
import Cart from "@/views/checkout/Cart/Cart";
import Checkout from "@/views/checkout/Checkout";
import UserBookList from "@/views/UserBookList";
import PublicBookList from "@/views/PublicBookList.vue";
import OrderComplete from "@/views/checkout/OrderComplete";
import { authGuard, authAdminRoleGuard } from "@/auth/authGuard";
import OrderPending from "@/views/checkout/OrderPending";

/**
 * Auth guard that skips authentication for Buy Now flow.
 * Allows unauthenticated customers to checkout via Buy Now.
 *
 * NOTE for developer working on authentication:
 * This guard bypasses auth when ?flow=buyNow is in the URL.
 * Verify that downstream pages (Checkout, OrderPending, OrderComplete)
 * handle both guest and signed-in customers correctly.
 */
const authGuardUnlessBuyNow = (to, from, next) => {
  if (to.query.flow === 'buyNow') {
    return next();
  }
  return authGuard(to, from, next);
};
import FilteredBookList from "@/views/FilteredBookList";
import VerificationPending from "@/views/VerificationPending";
import PublisherBookList from "@/views/PublisherBookList";
import AuthorPage from "@/views/AuthorPage";
import i18n from "@/i18n";
import Admin from "@/views/admin/Admin";
import AdminDashboard from "@/views/admin/AdminDashboard";
import TransactionReport from "@/views/admin/TransactionReport";
import CouponManager from "@/views/admin/CouponManager";
import SearchResults from "@/views/SearchResults";
import RefundPolicy from "@/views/RefundPolicy";
import UserDashboard from "@/views/admin/UserDashboard";
import EventManager from "@/views/admin/EventManager";
import CustomerDashboard from "@/views/admin/CustomerDashboard";
import Customer from "@/views/admin/Customer";
import OrderOverview from "@/views/admin/OrderOverview";
import EventDashboard from "@/views/admin/EventDashboard";
import EventOverview from "@/views/admin/EventOverview.vue";
import SchoolBookListManager from "@/views/admin/SchoolBookListManager";
import SchoolBookLists from "@/views/SchoolBookLists";
import HideMobileNav from "@/views/HideMobileNav";
import B2bCustomers from "@/views/admin/B2bCustomers.vue";
import ProductsDatabase from "@/views/admin/ProductsDatabase/ProductsDatabase.vue";
import OrderReport from "@/views/admin/OrderReport.vue";
import FeaturedBookManager from "@/views/admin/FeaturedBookManager.vue";
import AdminBookLists from "@/views/admin/AdminBookLists.vue";
import store from "@/store";
import { normalizeLocaleCode, getHreflangForLocale } from "@/helpers/localeUrlHelper";

Vue.use(VueRouter);

function redirectLegacyBookIriRoute(to, next) {
  const slug = to?.params?.slug;

  if (
    typeof slug === "string" &&
    (slug.startsWith("/books/") || slug.startsWith("/local_books/"))
  ) {
    next({
      name: "book",
      params: { id: slug },
      query: to.query,
      hash: to.hash,
      replace: true,
    });
    return true;
  }

  return false;
}

const routes = [
  {
    path: "/",
    name: "home",
    component: Home,
  },
  {
    path: "/hide-mobile-nav/:hide",
    name: "hideMobileNav",
    component: HideMobileNav,
  },
  {
    path: "/browse/:code/:name",
    name: "browseBySubject",
    component: FilteredBookList,
  },
  {
    path: "/bookshelf/",
    name: "bookshelf",
    component: Bookshelf,
    beforeEnter: authGuard,
  },
  {
    path: "/user-book-list/:id",
    name: "userBookList",
    component: UserBookList,
  },
  {
    path: "/q/:slug",
    name: "publicBookList",
    component: PublicBookList,
    meta: { showReturnBar: true },
  },
  {
    path: "/book/:slug",
    name: "bookBySlug",
    component: BookInfo,
    meta: {
      showReturnBar: true,
      returnBarToRouteName: "home",
      returnBarTextLoc: "shop.continueShopping",
    },
    beforeEnter(to, from, next) {
      if (!redirectLegacyBookIriRoute(to, next)) {
        next();
      }
    },
  },
  {
    path: "/local-book/:slug",
    name: "localBookBySlug",
    component: BookInfo,
    meta: {
      showReturnBar: true,
      returnBarToRouteName: "home",
      returnBarTextLoc: "shop.continueShopping",
    },
    beforeEnter(to, from, next) {
      if (!redirectLegacyBookIriRoute(to, next)) {
        next();
      }
    },
  },
  {
    path: "/book/:id/:fromEmail?",
    name: "book",
    component: BookInfo,
    meta: {
      showReturnBar: true,
      returnBarToRouteName: "home",
      returnBarTextLoc: "shop.continueShopping",
    },
  },
  {
    path: "/reader/:id",
    name: "reader",
    component: BookReader,
    beforeEnter: authGuard,
  },
  {
    path: "/cart/",
    name: "cart",
    component: Cart,
    meta: {
      showReturnBar: true,
      returnBarToRouteName: "home",
      returnBarTextLoc: "checkout.backToStore",
    },
  },
  {
    path: "/checkout/",
    name: "checkout",
    component: Checkout,
    meta: {
      showReturnBar: true,
      returnBarToRouteName: "home",
      returnBarTextLoc: "checkout.backToStore",
    },
    beforeEnter: authGuardUnlessBuyNow,
  },
  {
    // NOTE for developer working on authentication:
    // Auth bypassed for Buy Now guest orders via authGuardUnlessBuyNow.
    // Verify OrderComplete handles guest customers (no bookshelf, no account).
    path: "/confirmed/:id",
    name: "confirmed",
    component: OrderComplete,
    meta: {
      showReturnBar: true,
      returnBarToRouteName: "home",
      returnBarTextLoc: "checkout.backToStore",
    },
    beforeEnter: authGuardUnlessBuyNow,
  },
  {
    path: "/contact/",
    name: "contact",
    component: Contact,
  },
  {
    path: "/profile/:snackbar?",
    name: "userProfile",
    component: UserProfile,
    beforeEnter: authGuard,
  },
  {
    path: "/edit-profile",
    name: "editUserProfile",
    component: EditUserProfile,
    beforeEnter: authGuard,
  },
  {
    path: "/order-pending/:id/:safaricomMpesaRequestId?/:momoMtnRequestId?",
    name: "orderPending",
    component: OrderPending,
    beforeEnter: authGuardUnlessBuyNow,
    meta: {
      showReturnBar: true,
    },
  },
  {
    path: "/publisher/:slug",
    name: "publisher",
    component: PublisherBookList,
    props: true,
    meta: {
      showReturnBar: true,
      returnBarToRouteName: "home",
      returnBarTextLoc: "shop.continueShopping",
    },
  },
  {
    path: "/publisher-book-list/:id",
    name: "publisherBookList",
    component: () => import("@/views/PublisherBookListRedirect.vue"),
    props: true,
  },
  {
    path: "/author/:id",
    name: "author",
    component: AuthorPage,
    props: true,
    meta: {
      showReturnBar: true,
      returnBarToRouteName: "home",
      returnBarTextLoc: "shop.continueShopping",
    },
  },
  {
    path: "/verification-pending/",
    name: "verificationPending",
    component: VerificationPending,
    beforeEnter: authGuard,
  },
  {
    path: "/admin/",
    component: Admin,
    beforeEnter: authAdminRoleGuard,
    redirect: { name: "adminDashboard" },
    children: [
      {
        path: "admin-dashboard",
        name: "adminDashboard",
        component: AdminDashboard,
      },
      {
        path: "order-report",
        name: "orderReport",
        component: OrderReport,
      },
      {
        path: "transaction-report",
        name: "transactionReport",
        component: TransactionReport,
      },
      {
        path: "coupon-manager",
        name: "couponManager",
        component: CouponManager,
      },
      {
        path: "user-dashboard",
        name: "userDashboard",
        component: UserDashboard,
      },
      {
        path: "customer-dashboard",
        name: "customerDashboard",
        component: CustomerDashboard,
        meta: {
          breadCrumbs() {
            return [
              {
                text: i18n.t("routes.adminDashboard"),
                to: { name: "adminDashboard" },
                disabled: false,
              },
              {
                text: i18n.t("routes.customerDashboard"),
                to: { name: "customerDashboard" },
                disabled: false,
              },
            ];
          },
        },
      },
      {
        path: "customer/:id/:customerName?",
        name: "customer",
        component: Customer,
        meta: {
          breadCrumbs(route) {
            const id = route.params.id;
            const customerName = route.params.customerName;
            const fromOrderReport = route.params.fromOrderReport
            return [
              {
                text: i18n.t("routes.adminDashboard"),
                to: { name: "adminDashboard" },
                disabled: false,
              },
              (fromOrderReport ?
                { 
                  text: i18n.t("routes.orderReport"),
                  to: { name: "orderReport" },
                  disabled: false,
                } :
                {
                  text: i18n.t("routes.customerDashboard"),
                  to: { name: "customerDashboard" },
                  disabled: false,
                }
              ),
              {
                text: customerName ?? i18n.t("routes.customer"),
                to: {
                  name: "customer",
                  params: { id: id, customerName: customerName },
                },
                params: { id: id, customerName: customerName },
                disabled: false,
              },
            ];
          },
        },
      },
      {
        path: "order-overview/:orderId/:customerId?/:customerName?/:eventId?/:eventName?",
        name: "orderOverview",
        component: OrderOverview,
        meta: {
          breadCrumbs(route) {
            const orderId = route.params.orderId;
            const customerId = route.params.customerId;
            const customerName = route.params.customerName;
            const eventId = route.params.eventId;
            const eventName = route.params.eventName;
            const fromOrderReport = route.params.fromOrderReport;
            return [
              {
                text: i18n.t("routes.adminDashboard"),
                to: { name: "adminDashboard" },
                disabled: false,
              },
              ...(fromOrderReport
                ? [
                    {
                      text: i18n.t("routes.orderReport"),
                      to: { name: "orderReport" },
                      disabled: false,
                    },
                  ]
                : eventId
                ? [
                    {
                      text: i18n.t("routes.eventDashboard"),
                      to: { name: "eventDashboard" },
                      disabled: false,
                    },
                    {
                      text: eventName ?? i18n.t("routes.eventOverview"),
                      to: {
                        name: "eventOverview",
                        params: { id: eventId, eventName: eventName },
                      },
                      params: { id: eventId, eventName: eventName },
                      disabled: false,
                    },
                  ]
                : customerId
                ? [
                    {
                      text: i18n.t("routes.customerDashboard"),
                      to: { name: "customerDashboard" },
                      disabled: false,
                    },
                    {
                      text: customerName ?? i18n.t("routes.customer"),
                      to: {
                        name: "customer",
                        params: { id: customerId, customerName: customerName },
                      },
                      params: { id: customerId, customerName: customerName },
                      disabled: false,
                    },
                  ]
                : []),
              {
                text: i18n.t("common.order") + ": " + orderId,
                to: {
                  name: "orderOverview",
                  params: {
                    id: orderId,
                    customerId: customerId,
                    customerName: customerName,
                  },
                },
                params: {
                  id: orderId,
                  customerId: customerId,
                  customerName: customerName,
                },
                disabled: false,
              },
            ];
          },
        },
      },
      {
        path: "event-manager",
        name: "eventManager",
        component: EventManager,
      },
      {
        path: "event-dashboard",
        name: "eventDashboard",
        component: EventDashboard,
        meta: {
          breadCrumbs() {
            return [
              {
                text: i18n.t("routes.adminDashboard"),
                to: { name: "adminDashboard" },
                disabled: false,
              },
              {
                text: i18n.t("routes.eventDashboard"),
                to: { name: "eventDashboard" },
                disabled: false,
              },
            ];
          },
        },
      },
      {
        path: "event-overview/:id/:eventName?",
        name: "eventOverview",
        component: EventOverview,
        meta: {
          breadCrumbs(route) {
            const id = route.params.id;
            const eventName = route.params.eventName;
            return [
              {
                text: i18n.t("routes.adminDashboard"),
                to: { name: "adminDashboard" },
                disabled: false,
              },
              {
                text: i18n.t("routes.eventDashboard"),
                to: { name: "eventDashboard" },
                disabled: false,
              },
              {
                text: eventName ?? i18n.t("routes.eventOverview"),
                to: {
                  name: "eventOverview",
                  params: { id: id, eventName: eventName },
                },
                params: { id: id, eventName: eventName },
                disabled: false,
              },
            ];
          },
        },
      },
      {
        path: "school-book-list-manager",
        name: "schoolBookListManager",
        component: SchoolBookListManager,
      },
      {
        path: "products-database",
        name: "productsDatabase",
        component: ProductsDatabase,
      },
      {
        path: "booklists",
        name: "adminBookLists",
        component: AdminBookLists,
      },
      {
        path: "featured-book-manager",
        name: "featuredBookManager",
        component: FeaturedBookManager,
      },
    ],
  },
  {
    path: "/search/:searchTerm/",
    name: "searchResults",
    component: SearchResults,
    meta: {
      showReturnBar: true,
      returnBarToRouteName: "home",
      returnBarTextLoc: "checkout.backToStore",
    },
  },
  {
    path: "/refund-policy/",
    name: "refundPolicy",
    component: RefundPolicy,
  },
  {
    path: "/school-book-lists/",
    name: "schoolBookLists",
    component: SchoolBookLists,
  },
  {
    path: "/book-list/:id",
    name: "bookList",
    component: UserBookList,
    meta: {
      showReturnBar: true,
    },
  },
  {
    path: "/b2b-customers/",
    name: "b2bCustomers",
    component: B2bCustomers,
  },
  {
    path: "*",
    name: "notFound",
    component: NotFound,
  },
];

const router = new VueRouter({
  mode: "history",
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

function syncLocaleFromRoute(route) {
  const localeCode = normalizeLocaleCode(route?.query?.lang);

  if (localeCode && store.getters.languageCode !== localeCode) {
    store.dispatch("changeLocale", localeCode);
  }
}

function buildFallbackDocumentTitle(route) {
  const routeName = route?.name || "home";
  return i18n.t("common.siteTitle", { route: i18n.t(`routes.${routeName}`) });
}

syncLocaleFromRoute(router.currentRoute);
window.document.title = buildFallbackDocumentTitle(router.currentRoute);

router.beforeEach((to, from, next) => {
  syncLocaleFromRoute(to);
  window.document.title = buildFallbackDocumentTitle(to);

  // Normalize 3-letter locale codes in ?lang= to 2-letter hreflang codes
  // to prevent SEO crawl equity split between ?lang=eng and ?lang=en
  const rawLang = to.query?.lang;
  if (rawLang) {
    const normalized = normalizeLocaleCode(rawLang);
    const hreflang = normalized ? getHreflangForLocale(normalized) : null;
    if (hreflang && rawLang !== hreflang) {
      next({ ...to, query: { ...to.query, lang: hreflang }, replace: true });
      return;
    }
  }

  next();
});

export default router;
